import React, { useEffect, useState } from "react";
import ControlLayout from "../components/ControlLayout";
import { api } from "../lib/api";
import { Download, ShieldCheck } from "lucide-react";

export default function ControlOps() {
  const [snap, setSnap] = useState(null);
  const [payroll, setPayroll] = useState(null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);

  const refresh = async (d) => {
    setLoading(true);
    const [s, p] = await Promise.all([
      api.get(`/ops/daily-snapshot?date=${d}`),
      api.get("/payroll/eligible"),
    ]);
    setSnap(s.data);
    setPayroll(p.data);
    setLoading(false);
  };

  useEffect(() => { refresh(date); /* eslint-disable-next-line */ }, [date]);

  const dlSnap = () => {
    const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `presence-snapshot-${date}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <ControlLayout
      title="Controlled Rollout"
      subtitle="Daily operational log · payroll boundary · launch safety"
      actions={
        <div className="flex gap-2 items-center">
          <input
            data-testid="ops-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 px-3 border border-neutral-300 font-mono text-sm"
          />
          <button
            data-testid="ops-download"
            onClick={dlSnap}
            disabled={!snap}
            className="h-11 px-4 border border-neutral-900 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download size={14} /> Export JSON
          </button>
        </div>
      }
    >
      {loading && <div className="mono-label">Loading…</div>}
      {!loading && snap && (
        <div className="grid grid-cols-12 gap-6">
          <Panel className="col-span-12 lg:col-span-4" title="Scans today" footer={`by source · rule ${snap.payroll_rule_version}`}>
            <Metric label="Total" value={snap.scans.total} testid="m-total" />
            <Metric label="Accepted" value={snap.scans.accepted} tone="ok" testid="m-accepted" />
            <Metric label="Review" value={snap.scans.review} tone={snap.scans.review > 0 ? "warn" : "neutral"} testid="m-review" />
            <Metric label="Rejected" value={snap.scans.rejected} tone={snap.scans.rejected > 0 ? "fail" : "neutral"} testid="m-rejected" />
            <div className="mt-3 pt-3 border-t border-neutral-200 grid grid-cols-2 gap-2">
              <Metric label="Terminal" value={snap.scans.by_source.TERMINAL} small testid="m-terminal" />
              <Metric label="PWA" value={snap.scans.by_source.PWA} small testid="m-pwa" />
            </div>
          </Panel>

          <Panel className="col-span-12 lg:col-span-4" title="Security signals today" footer="zero-trust counters">
            <Metric label="Identity mismatch" value={snap.security.identity_mismatch} tone={snap.security.identity_mismatch > 0 ? "fail" : "ok"} testid="m-identity-mismatch" />
            <Metric label="Fingerprint mismatch" value={snap.security.fingerprint_mismatch} tone={snap.security.fingerprint_mismatch > 0 ? "warn" : "ok"} testid="m-fp-mismatch" />
            <Metric label="Duplicate replay" value={snap.security.duplicate_replay} tone="neutral" testid="m-dup-replay" />
            <Metric label="Manual override" value={snap.security.manual_override} tone={snap.security.manual_override > 0 ? "warn" : "neutral"} testid="m-override" />
          </Panel>

          <Panel className="col-span-12 lg:col-span-4" title="Payroll boundary" footer={snap.note}>
            <Metric label="ELIGIBLE today" value={snap.payroll.ELIGIBLE_today} tone="ok" testid="m-eligible-today" />
            <Metric label="PENDING_REVIEW today" value={snap.payroll.PENDING_REVIEW_today} tone="warn" testid="m-pending-today" />
            <Metric label="EXCLUDED today" value={snap.payroll.EXCLUDED_today} tone="fail" testid="m-excluded-today" />
            <Metric label="pending_sync (client-only)" value={snap.pending_sync === null ? "—" : snap.pending_sync} small testid="m-pending-sync" />
          </Panel>

          <Panel className="col-span-12" title="Payroll eligible — all-time totals" footer={`authoritative via GET /api/payroll/eligible · rule ${payroll?.payroll_rule_version}`}>
            <div className="grid grid-cols-3 gap-4">
              <BigTotal label="ELIGIBLE" value={payroll?.totals?.ELIGIBLE} tone="ok" testid="big-eligible" />
              <BigTotal label="PENDING_REVIEW" value={payroll?.totals?.PENDING_REVIEW} tone="warn" testid="big-pending" />
              <BigTotal label="EXCLUDED" value={payroll?.totals?.EXCLUDED} tone="fail" testid="big-excluded" />
            </div>
            <div className="mt-4 flex items-center gap-2 mono-label">
              <ShieldCheck size={14} />
              Payroll systems must integrate via <code className="font-mono">/api/payroll/eligible</code>. Other statuses never reach payroll.
            </div>
          </Panel>

          <Panel className="col-span-12" title="Recent ELIGIBLE records (top 20)">
            <table className="w-full text-sm" data-testid="eligible-table">
              <thead className="bg-[#FAFAFA] border-b border-neutral-200">
                <tr className="text-left">
                  <Th>Employee</Th><Th>Source</Th><Th>Check-in</Th><Th>Check-out</Th><Th>Outlet</Th><Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {(payroll?.records || []).slice(0, 20).map((r) => (
                  <tr key={r.id} className="border-b border-neutral-100">
                    <td className="px-4 py-2 font-mono text-xs">{r.employee_id}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.source}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.check_in_at_iso?.slice(0, 19)?.replace("T", " ")}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.check_out_at_iso?.slice(0, 19)?.replace("T", " ") || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs">{r.outlet_id}</td>
                    <td className="px-4 py-2">
                      <span className="px-2 py-0.5 border border-[#22C55E] bg-[#DCFCE7] text-[#166534] font-mono text-[10px] uppercase tracking-wider">
                        {r.payroll_status}
                      </span>
                    </td>
                  </tr>
                ))}
                {(payroll?.records || []).length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center mono-label">No eligible records yet.</td></tr>
                )}
              </tbody>
            </table>
          </Panel>
        </div>
      )}
    </ControlLayout>
  );
}

function Panel({ title, children, footer, className = "" }) {
  return (
    <div className={`panel ${className}`}>
      <div className="panel-header"><span className="mono-label">{title}</span></div>
      <div className="p-4 space-y-2">{children}</div>
      {footer && <div className="px-4 py-2 border-t border-neutral-200 mono-label text-neutral-500" style={{ fontSize: 9 }}>{footer}</div>}
    </div>
  );
}

function Metric({ label, value, tone = "neutral", small = false, testid }) {
  const cls = tone === "ok" ? "text-[#166534]" : tone === "warn" ? "text-[#854D0E]" : tone === "fail" ? "text-[#991B1B]" : "text-neutral-900";
  return (
    <div data-testid={testid} className="flex items-baseline justify-between">
      <span className="mono-label" style={{ fontSize: 10 }}>{label}</span>
      <span className={`font-mono font-semibold ${small ? "text-sm" : "text-xl"} ${cls}`}>{value ?? "—"}</span>
    </div>
  );
}

function BigTotal({ label, value, tone, testid }) {
  const cls = tone === "ok" ? "text-[#166534] border-[#22C55E] bg-[#DCFCE7]"
    : tone === "warn" ? "text-[#854D0E] border-[#EAB308] bg-[#FEF9C3]"
    : "text-[#991B1B] border-[#EF4444] bg-[#FEE2E2]";
  return (
    <div data-testid={testid} className={`border p-4 ${cls}`}>
      <div className="mono-label" style={{ fontSize: 10 }}>{label}</div>
      <div className="font-mono font-black text-4xl mt-1">{value ?? "—"}</div>
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-2 mono-label text-left" style={{ fontSize: 10 }}>{children}</th>;
